import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { Filter, SortAsc, X, ChevronDown } from 'lucide-react';
import Layout from '../components/layout/Layout';
import ProductCard from '../components/products/ProductCard';
import CategoryFilters from '../components/products/CategoryFilters';
import Pagination from '../components/common/Pagination';
import * as Types from '../services/api/api.types';
import { useCart } from '../contexts/CartContext';
import { apiService } from '../services/api/instance';
import { useScrollRestoration } from '../hooks/useScrollRestoration';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import {
    CategoryFilterState,
    EMPTY_FILTERS,
    activeFilterChips,
    applyFilters,
    buildFacets,
    countActiveFilters,
    // countHiddenByMissingSpec,
    effectivePrice,
    pruneAttributes,
} from '../utils/productFilters';
import logger from '../utils/logger';

type SortOption = 'relevance' | 'price-asc' | 'price-desc' | 'name';

const SORT_LABELS: Record<SortOption, { short: string; long: string }> = {
    relevance: { short: 'Подходящи', long: 'Най-подходящи' },
    'price-asc': { short: 'Цена ↑', long: 'Цена: ниска към висока' },
    'price-desc': { short: 'Цена ↓', long: 'Цена: висока към ниска' },
    name: { short: 'Име', long: 'Име: А-Я' },
};

// The page number lives in the URL (?page=N), which history already restores.
interface RestoredState {
    sortBy: SortOption;
    filters: CategoryFilterState;
}

const CategoryPage: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const [searchParams, setSearchParams] = useSearchParams();
    const [allProducts, setAllProducts] = useState<Types.ProductFull[]>([]);
    const [category, setCategory] = useState<Types.CategoryFull | null>(null);
    const [intro, setIntro] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const currentPage = Math.max(1, parseInt(searchParams.get('page') ?? '', 10) || 1);
    const [showFilters, setShowFilters] = useState(false);
    const [sortBy, setSortBy] = useState<SortOption>('price-asc');
    const [filters, setFilters] = useState<CategoryFilterState>(EMPTY_FILTERS);
    const [showSortDropdown, setShowSortDropdown] = useState(false);
    const { cart } = useCart();
    const productsPerPage = 12;

    const pageLink = (page: number) => ({ search: page > 1 ? `?page=${page}` : '' });
    // Filter changes replace the entry: the filters are not in the URL, so a
    // back step to "page 1 of the same listing" would be a no-op.
    const goToPage = (page: number, replace = false) => setSearchParams(pageLink(page).search, { replace });

    // One resolve feeds both the header and the listing. The backend accepts a
    // numeric id here too, for links made before the category had a slug.
    useEffect(() => {
        if (!slug) return;
        let cancelled = false;

        const fetchCategory = async () => {
            setLoading(true);
            setIntro(null);
            try {
                const categoryId = await apiService.resolveCategorySlug(slug);
                if (cancelled) return;
                if (!categoryId) {
                    setCategory(null);
                    setAllProducts([]);
                    return;
                }

                const categories = await apiService.getCategories();
                if (cancelled) return;
                setCategory(categories.find(cat => cat.id === categoryId) ?? null);

                // Optional copy: a failure here must not cost the listing.
                apiService.getCategoryDescription(categoryId)
                    .then(text => { if (!cancelled) setIntro(text); })
                    .catch(error => logger.error('Грешка при зареждането на описанието на категорията:', error));

                // The whole category: this page filters, sorts and pages it
                // itself, and a capped fetch would only ever hand it the
                // suppliers the backend happened to list first.
                const products = await apiService.getAllProductsByCategory(categoryId);
                if (!cancelled) setAllProducts(products);
            } catch (error) {
                logger.error('Грешка при зареждането на категорията:', error);
                if (!cancelled) setAllProducts([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetchCategory();
        return () => {
            cancelled = true;
        };
    }, [slug]);

    // Moving to another category starts from a clean sidebar - its manufacturers
    // and specs are a different set. The ref keeps this off the first render, so
    // a restored snapshot survives the mount that would otherwise clear it.
    const previousCategoryRef = useRef(slug);
    useEffect(() => {
        if (previousCategoryRef.current !== slug) {
            previousCategoryRef.current = slug;
            setFilters(EMPTY_FILTERS);
        }
    }, [slug]);

    // Page links navigate through the router; start each page at its top. The
    // ref keeps this off the mount, where scroll restoration owns the offset.
    const previousPageRef = useRef(currentPage);
    useEffect(() => {
        if (previousPageRef.current !== currentPage) {
            previousPageRef.current = currentPage;
            window.scrollTo(0, 0);
        }
    }, [currentPage]);

    // A spec the feed has stopped carrying would filter the listing down to
    // nothing, with no checkbox left on screen to explain it.
    useEffect(() => {
        if (allProducts.length === 0) return;
        setFilters(current => {
            const pruned = pruneAttributes(current, allProducts);
            return JSON.stringify(pruned.attributes) === JSON.stringify(current.attributes)
                ? current
                : pruned;
        });
    }, [allProducts]);

    const facets = useMemo(() => buildFacets(allProducts, filters), [allProducts, filters]);

    // Products a spec filter is holding back only because their supplier never
    // stated that spec - not because they fail it. Restore alongside the banner
    // in the listing below.
    // const hiddenByMissingSpec = useMemo(
    //     () => countHiddenByMissingSpec(allProducts, filters),
    //     [allProducts, filters],
    // );

    const filteredProducts = useMemo(() => {
        const filtered = applyFilters(allProducts, filters);

        // In-stock first, then each group ordered on its own, so a cheap item
        // nobody can buy never outranks one that ships.
        const inStock = filtered.filter(p => p.status === 1);
        const outOfStock = filtered.filter(p => p.status !== 1);

        const sortProducts = (products: Types.ProductFull[]) => {
            switch (sortBy) {
                case 'price-asc':
                    products.sort((a, b) => effectivePrice(a) - effectivePrice(b));
                    break;
                case 'price-desc':
                    products.sort((a, b) => effectivePrice(b) - effectivePrice(a));
                    break;
                case 'name':
                    products.sort((a, b) => {
                        const nameA = a.name.find(n => n.language_code === 'bg')?.text || '';
                        const nameB = b.name.find(n => n.language_code === 'bg')?.text || '';
                        return nameA.localeCompare(nameB);
                    });
                    break;
                case 'relevance':
                    // Keep the original order from backend
                    break;
            }
        };

        sortProducts(inStock);
        sortProducts(outOfStock);

        return [...inStock, ...outOfStock];
    }, [allProducts, filters, sortBy]);

    const totalPages = Math.ceil(filteredProducts.length / productsPerPage);

    // Only clamp once there is something to page through - during the initial
    // load the count is 0 and would throw away a restored page.
    useEffect(() => {
        if (totalPages > 0 && currentPage > totalPages) {
            goToPage(1, true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [totalPages, currentPage]);

    // Coming back from a product returns to the same page, filters and offset.
    useScrollRestoration<RestoredState>({
        ready: !loading,
        state: { sortBy, filters },
        onRestore: saved => {
            setSortBy(saved.sortBy ?? 'price-asc');
            // Snapshots taken before the sidebar grew these filters carry no
            // `filters` key; an empty sidebar is the honest reading of those.
            setFilters({ ...EMPTY_FILTERS, ...(saved.filters ?? {}) });
        },
    });

    const getBgName = (names?: Types.Name[]): string => {
        if (!names) return '';
        const bgName = names.find(name => name.language_code === 'bg');
        return bgName ? bgName.text : '';
    };

    useDocumentTitle(category ? getBgName(category.name) : null);

    // Any narrowing invalidates the page the shopper was on: page 7 of the old
    // listing is rarely page 7 of the new one.
    const changeFilters = (next: CategoryFilterState) => {
        setFilters(next);
        goToPage(1, true);
    };

    const clearFilters = () => {
        setFilters(EMPTY_FILTERS);
        goToPage(1, true);
    };

    const chips = activeFilterChips(filters);
    const activeCount = countActiveFilters(filters);

    return (
        <Layout cartItemsCount={cart.items.length} cartTotal={cart.total}>
            <div className="container mx-auto py-6">
                <div className="mb-6">
                    <div className="text-sm mb-2 text-gray-600">
                        <Link to="/" className="hover:text-primary-600">Начало</Link>
                        {' > '}
                        <span className="font-medium">{category ? getBgName(category.name) : ''}</span>
                    </div>
                    <div className="flex items-center justify-between">
                        <h1 className="text-2xl font-bold">{category ? getBgName(category.name) : 'Категория'}</h1>
                        {activeCount > 0 && (
                            <button
                                onClick={clearFilters}
                                className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
                            >
                                <X className="h-4 w-4" />
                                Изчисти филтрите
                            </button>
                        )}
                    </div>
                    {intro && currentPage === 1 && (
                        <p className="mt-3 max-w-3xl text-gray-700 whitespace-pre-line">{intro}</p>
                    )}
                </div>

                <div className="flex flex-col md:flex-row gap-6">
                    {/* Филтри: a sidebar on a desktop, a sheet over the listing on a phone. */}
                    <div
                        className={`md:w-1/4 md:block ${
                            showFilters
                                // Above the sticky mobile nav, which is z-50.
                                ? 'fixed inset-0 z-[60] bg-black/50 p-4 overflow-y-auto md:static md:z-auto md:bg-transparent md:p-0 md:overflow-visible'
                                : 'hidden'
                        }`}
                        onClick={showFilters ? () => setShowFilters(false) : undefined}
                    >
                        <div onClick={e => e.stopPropagation()}>
                            <CategoryFilters
                                filters={filters}
                                facets={facets}
                                onChange={changeFilters}
                                onClear={clearFilters}
                                activeCount={activeCount}
                                onClose={() => setShowFilters(false)}
                            />
                        </div>
                    </div>

                    {/* Продукти */}
                    <div className="flex-1">
                        <div className="flex justify-between items-center mb-4">
                            <p className="text-gray-600">
                                Намерени: <span className="font-medium">{filteredProducts.length}</span> продукта
                            </p>

                            <div className="flex items-center gap-2">
                                {/* Sort Dropdown */}
                                <div className="relative">
                                    <button
                                        onClick={() => setShowSortDropdown(!showSortDropdown)}
                                        className="flex items-center text-sm bg-white border border-gray-300 hover:bg-gray-50 px-3 py-2 rounded-lg transition-colors"
                                    >
                                        <SortAsc className="h-4 w-4 mr-2" />
                                        <span>{SORT_LABELS[sortBy].short}</span>
                                        <ChevronDown className="h-4 w-4 ml-1" />
                                    </button>

                                    {showSortDropdown && (
                                        <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                                            {(Object.keys(SORT_LABELS) as SortOption[]).map(option => (
                                                <button
                                                    key={option}
                                                    onClick={() => { setSortBy(option); setShowSortDropdown(false); }}
                                                    className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${sortBy === option ? 'bg-primary-50 text-primary-700' : ''}`}
                                                >
                                                    {SORT_LABELS[option].long}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <button
                                    onClick={() => setShowFilters(true)}
                                    className="md:hidden flex items-center text-sm bg-gray-100 hover:bg-gray-200 px-3 py-2 rounded-lg"
                                >
                                    <Filter className="h-4 w-4 mr-1" />
                                    Филтри
                                    {activeCount > 0 && (
                                        <span className="ml-1 bg-primary-600 text-white text-xs px-1.5 py-0.5 rounded-full">
                                            {activeCount}
                                        </span>
                                    )}
                                </button>
                            </div>
                        </div>

                        {/*
                          * Held back for now. This offered to re-admit the
                          * products a spec filter drops only because their
                          * supplier never stated that spec - see
                          * countHiddenByMissingSpec, and the commented-out
                          * hiddenByMissingSpec above. The filtering itself is
                          * unaffected: includeUnspecified stays false without
                          * this control, which is the previous behaviour.
                          *
                        {hiddenByMissingSpec > 0 && (
                            <div className="flex flex-wrap items-center justify-between gap-2 mb-4 bg-amber-50 border border-amber-200 text-amber-900 text-sm rounded-lg px-3 py-2">
                                <span>
                                    {filters.includeUnspecified
                                        ? `Показани са и ${hiddenByMissingSpec} продукта, за които тази спецификация не е посочена.`
                                        : `${hiddenByMissingSpec} продукта са скрити, защото доставчикът им не е посочил тази спецификация.`}
                                </span>
                                <button
                                    onClick={() => changeFilters({
                                        ...filters,
                                        includeUnspecified: !filters.includeUnspecified,
                                    })}
                                    className="shrink-0 underline hover:no-underline font-medium"
                                >
                                    {filters.includeUnspecified ? 'Скрий ги' : 'Покажи и тях'}
                                </button>
                            </div>
                        )}
                          */}

                        {chips.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-4">
                                {chips.map(chip => (
                                    <button
                                        key={chip.key}
                                        onClick={() => changeFilters(chip.remove(filters))}
                                        className="flex items-center gap-1 bg-primary-50 text-primary-700 border border-primary-200 text-xs px-2 py-1 rounded-full hover:bg-primary-100 transition-colors"
                                    >
                                        <span className="max-w-[16rem] truncate">{chip.label}</span>
                                        <X className="h-3 w-3 shrink-0" />
                                    </button>
                                ))}
                            </div>
                        )}

                        {loading ? (
                            <div className="flex justify-center py-8">
                                <p className="text-lg">Зареждане...</p>
                            </div>
                        ) : filteredProducts.length === 0 ? (
                            <div className="text-center py-8 bg-white border border-gray-200 rounded-lg">
                                <h2 className="text-xl font-medium mb-2">Няма намерени продукти</h2>
                                <p className="text-gray-600 mb-4">
                                    {activeCount > 0
                                        ? 'Нито един продукт не отговаря на избраните филтри.'
                                        : 'Опитайте с други филтри или разгледайте другите ни категории.'}
                                </p>
                                {activeCount > 0 ? (
                                    <button onClick={clearFilters} className="text-primary-600 hover:underline">
                                        Изчисти филтрите
                                    </button>
                                ) : (
                                    <Link to="/" className="text-primary-600 hover:underline">
                                        Върнете се към началната страница
                                    </Link>
                                )}
                            </div>
                        ) : (
                            <>
                                <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
                                    {filteredProducts
                                        .slice((currentPage - 1) * productsPerPage, currentPage * productsPerPage)
                                        .map((product) => (
                                            <ProductCard key={`${product.supplier ?? 'vali'}:${product.id}`} product={product} />
                                        ))}
                                </div>

                                {/* Пагинация */}
                                <Pagination
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    onPageChange={page => goToPage(page)}
                                    getPageLink={pageLink}
                                    maxVisiblePages={5}
                                />
                            </>
                        )}
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default CategoryPage;
